/**
 * Authentication for the company portal.
 *
 * Ways in, all ending in our own JWT:
 *
 * 1. **Inside the Lark client** — login-free (免登). `h5sdk.ready()`, then
 *    `tt.requestAccess()` for an authorization code. Silent, no user action.
 *    Unchanged by accounts beyond Lark.
 *
 * 2. **In an ordinary browser**, from the sign-in chooser (views/SignIn.vue):
 *    Lark's web OAuth, Microsoft, Google — each a redirect that comes back to
 *    `https://<portal host>/` with a code — or email (a link, or a 6-digit
 *    code typed in). Only the platforms GET /api/auth/providers lists are
 *    offered, so one environment switches a platform on without a build.
 *
 * Every redirect shares one redirect URI, so the `state` says who answered:
 * it is prefixed by provider (`ms.<random>`, shared-types portalSignIn.ts),
 * and the PKCE verifier, the nonce and what the round trip was *for* wait in
 * sessionStorage under it. A round trip is for signing in, for connecting a
 * data scope, or for the Google Workspace super-admin takeover; the last two
 * need the session the person already had, so they restore it first.
 *
 * An invitation into an organisation account (`#/invite?i=…&t=…`) is taken out
 * of the address bar at load, kept in sessionStorage across the round trip, and
 * sent with the sign-in of the account's platform (`invitation` in the body).
 *
 * Browser sessions persist in localStorage with the platform they signed in
 * with: a Lark JWT lives 30 days and is renewed by Lark's own refresh token,
 * the others live 24 hours and are renewed half way (refreshBufferFor). The
 * in-Lark path deliberately does not persist, because requestAccess is silent
 * anyway and also refreshes the Lark tokens the server holds for Base calls.
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type {
  AuthLoginResponse,
  AuthProvidersResponse,
  AuthRefreshResponse,
  ApiErrorResponse,
  ClientSessionResponse,
  ConnectionPurpose,
  EmailLinkProof,
  EmailStartRequest,
  EmailStartResponse,
  EmailVerifyRequest,
  GoogleConnectPlan,
  GoogleTakeoverResponse,
  InvitationPreviewResponse,
  InvitationProof,
  MicrosoftAdminConsentResultResponse,
  MicrosoftConnectResponse,
  OAuthProvider,
  Platform,
  RedirectProvider,
  SignInHints,
} from '@lark-automadoc/shared-types'
import {
  GOOGLE_ADMIN_PROOF_SCOPES,
  GOOGLE_SIGNIN_SCOPES,
  INVITATION_PORTAL_PATH,
  JWT_EXPIRATION_MS_BY_PLATFORM,
  LARK_OAUTH_SCOPES_BASE,
  LARK_OAUTH_SCOPES,
  MICROSOFT_SIGNIN_SCOPES,
  buildGoogleAuthorizeUrl,
  buildLarkAuthorizeUrl,
  buildMicrosoftAuthorizeUrl,
  isMicrosoftAdminConsentState,
  isMicrosoftConsentRequired,
  MICROSOFT_ADMIN_PAGE_PATH,
  MICROSOFT_ORGANISATION_APPROVAL_STATE,
  makeCodeVerifier,
  makeOAuthState,
  OAUTH_CALLBACK_PARAMS,
  parseSignInHints,
  pkceChallenge,
  providerOfOAuthState,
  randomUrlSafe,
  readEmailAuthLink,
  readInvitationLink,
  readJwtClaims,
  readOAuthCallback,
  refreshBufferFor,
  type OAuthCallback,
} from '@lark-automadoc/shared-types'
import { getLocale } from '@/locales/i18n'
import { clearApprovalRequestsCache } from '@/utils/approvalRequestsCache'

export interface User {
  userId: string
  name: string
  avatarUrl?: string
  tenantKey: string
  tenantName?: string
}

/**
 * `not_in_lark` is the signed-out browser: the sign-in chooser. The name is
 * kept from when Lark was the only way in; App.vue renders SignIn.vue for it.
 */
export type AuthState = 'initializing' | 'authenticated' | 'error' | 'not_in_lark' | 'app_not_installed'

/**
 * What a redirect round trip was started for. Microsoft's admin consent is not
 * one: its link is the API's (signed state `msac.…`, shared-types microsoft.ts)
 * and is usually opened by someone else, in another browser.
 */
type RoundTripIntent = 'signin' | 'connect' | 'takeover'

/** One round trip in flight: kept in sessionStorage while the browser is at the provider. */
interface PendingRoundTrip {
  state: string
  provider: RedirectProvider
  intent: RoundTripIntent
  purpose?: ConnectionPurpose
  /** PKCE verifier and OIDC nonce; empty for Lark, whose authorize step takes neither. */
  codeVerifier: string
  nonce: string
  redirectUri: string
  /** The hash route to return to (deep links survive the round trip). */
  returnTo?: string
  /** Google sign-in asked again with prompt=consent (GOOGLE_CONSENT_REQUIRED): never a third time. */
  consentRetry?: boolean
  /** Takeover: also take admin from whoever got it by arriving first. */
  demoteFirstUser?: boolean
  /** Google sign-in that also proves a Workspace super admin (admitted whatever the join policy). */
  adminProof?: boolean
  createdAt: number
}

/**
 * The outcome of a connect or takeover round trip, for the page it returns to
 * to show once: the redirect lands on the bare origin, so it cannot say it there.
 */
export type ConnectionsNotice =
  | { kind: 'connected'; provider: OAuthProvider; purpose: ConnectionPurpose }
  | { kind: 'consentRequired'; purpose: ConnectionPurpose; adminConsentUrl: string | null }
  | { kind: 'adminConsentGranted' }
  | { kind: 'tookOver' }
  | { kind: 'error'; message: string }

/** Survives the OAuth round trip; without it every reload is a new redirect. */
const SESSION_KEY = 'automadoc-company-session'
/** The round trip in flight, deliberately in sessionStorage — it dies with the tab. */
const ROUND_TRIP_KEY = 'automadoc-company-oauth-attempt'
/**
 * The previous release's Lark-only state and return route. A tab that left for
 * Lark's screen before this release was deployed comes back to it; reading
 * them once more lets that sign-in finish rather than fail as a mismatch.
 */
const LEGACY_STATE_KEY = 'automadoc-company-oauth-state'
const LEGACY_RETURN_KEY = 'automadoc-company-oauth-return'
/** A round trip older than this is abandoned (provider codes live minutes). */
const ROUND_TRIP_MAX_AGE_MS = 15 * 60 * 1000

interface StoredSession {
  jwt: string
  expiresAt: number
  user: User
  /** Absent on sessions stored before other platforms existed: Lark. */
  platform?: Platform
}

/**
 * OAuth callback parameters, read once at module load — before the router
 * reads the URL — and stripped as soon as they are consumed (a code is
 * single-use; leaving it in the URL invites a pointless second redemption).
 */
let callback: OAuthCallback | null = readOAuthCallback(
  // index.html has already moved the callback out of the URL, before the Lark SDK was
  // inserted (a third-party script that can read location.search); the URL is the
  // fallback for a page served without that inline script (the dev server's first load).
  new URLSearchParams(window.__automadocOAuthCallback ?? window.location.search),
)

/**
 * An emailed link: `#/auth/email?c=<challenge>&t=<token>` to sign in, or
 * `?i=<invitation>&t=<token>` to accept an invitation (the links the API mails,
 * shared-types emailAuth.ts). The token is a credential, so it is taken out of
 * the address bar here, before anything else runs (index.html does not load
 * Lark's SDK on such a URL either) — a malformed one too.
 */
let emailLink: EmailLinkProof | null = (() => {
  if (!window.location.hash.startsWith('#/auth/email')) return null
  const proof = readEmailAuthLink(window.location.hash)
  window.history.replaceState(window.history.state, '', `${window.location.pathname}${window.location.search}#/`)
  return proof
})()

/** An organisation invitation waiting for its sign-in; sessionStorage, so it dies with the tab. */
const INVITATION_KEY = 'automadoc-company-invitation'

/**
 * An invitation link (`#/invite?i=<id>&t=<token>`, shared-types emailAuth.ts). The
 * token is a credential: out of the address bar at load, before anything else
 * runs, and kept for the sign-in that accepts it.
 */
const invitationAtLoad: InvitationProof | null = (() => {
  // index.html has already moved it out of the URL; the URL is the fallback for a page
  // served without that inline script (the dev server's first load).
  const hash = window.__automadocInvitation ?? window.location.hash
  // Read once: nothing else on the page needs the token.
  delete window.__automadocInvitation
  if (!hash.startsWith(INVITATION_PORTAL_PATH)) return null
  if (window.location.hash.startsWith(INVITATION_PORTAL_PATH)) {
    window.history.replaceState(window.history.state, '', `${window.location.pathname}${window.location.search}#/`)
  }
  return readInvitationLink(hash)
})()

function readStoredInvitation(): InvitationProof | null {
  try {
    if (invitationAtLoad) {
      sessionStorage.setItem(INVITATION_KEY, JSON.stringify(invitationAtLoad))
      return invitationAtLoad
    }
    const raw = sessionStorage.getItem(INVITATION_KEY)
    const parsed = raw ? (JSON.parse(raw) as Partial<InvitationProof>) : null
    return parsed && typeof parsed.invitationId === 'string' && typeof parsed.token === 'string'
      ? { invitationId: parsed.invitationId, token: parsed.token }
      : null
  } catch {
    // No storage: the invitation still works for a sign-in that needs no round trip.
    return invitationAtLoad
  }
}

/**
 * Who a client app's link-out asked us to sign in (`#/billing?provider=…&hint=…`,
 * 40-client-apps-design.md §1.7), read once at load.
 */
const entryHints: SignInHints = (() => {
  const query = window.location.hash.split('?')[1]
  if (!query) return {}
  return parseSignInHints(Object.fromEntries(new URLSearchParams(query)))
})()

function stripCallbackParams(): void {
  const url = new URL(window.location.href)
  let changed = false
  for (const name of OAUTH_CALLBACK_PARAMS) {
    if (url.searchParams.has(name)) {
      url.searchParams.delete(name)
      changed = true
    }
  }
  if (!changed) return
  // Preserves history.state: this runs after vue-router is installed, and
  // nulling its bookkeeping makes it warn and lose scroll restoration.
  window.history.replaceState(window.history.state, '', url.toString())
}

/**
 * Where every provider sends the browser back.
 *
 * Must be *byte-identical* in the authorize request and in the later code
 * exchange — the providers compare them — and registered on each provider's
 * app (Lark developer console, Entra app registration, Google OAuth client).
 */
function oauthRedirectUri(): string {
  return `${window.location.origin}/`
}

/** The current hash route, if it is one of ours (anything else came from outside). */
function currentRoute(): string | undefined {
  return window.location.hash.startsWith('#/') ? window.location.hash : undefined
}

/**
 * What a sign-in answers. Lark's /api/auth/login and Google's /login answer the
 * login shape (`jwt`); Microsoft's /login and email's /verify answer the
 * session shape every non-Lark client gets (ClientSessionResponse, `token`).
 */
type SignInAnswer = AuthLoginResponse | ClientSessionResponse

/** A message key the views translate, or the server's own sentence (te() tells them apart). */
class AuthApiError extends Error {
  constructor(message: string, readonly code?: string, readonly status?: number, readonly body?: Record<string, unknown>) {
    super(message)
  }
}

export const useAuthStore = defineStore('auth', () => {
  // State
  const state = ref<AuthState>('initializing')
  const user = ref<User | null>(null)
  const token = ref<string | null>(null)
  const expiresAt = ref<number | null>(null)
  /** The platform this session signed in with (the JWT's `plat`; absent is Lark). */
  const platform = ref<Platform>('lark')
  const error = ref<string | null>(null)
  /** Something the chooser should say that is not a failure (an admin's consent recorded). */
  const notice = ref<'adminConsentDone' | null>(null)
  /**
   * A Microsoft sign-in its organisation's policy stopped (MS_CONSENT_REQUIRED):
   * the admin-consent link the API made for it, for the chooser to offer.
   */
  const signInConsentUrl = ref<string | null>(null)
  /** The last Microsoft sign-in ended in a plain "declined": the chooser mentions the IT admin page. */
  const microsoftDeclined = ref(false)
  /** True from the moment a redirect is triggered until the page goes. */
  const oauthRedirecting = ref(false)
  /**
   * True when this session came from a browser sign-in.
   *
   * Only such a session can be signed out of in any meaningful sense: the
   * in-Lark path re-authenticates silently on every load, so a "sign out" there
   * would undo itself before the customer saw it.
   */
  const isBrowserSession = ref(false)
  /** What this environment offers; null until GET /api/auth/providers has answered. */
  const providers = ref<AuthProvidersResponse | null>(null)
  /** The last GET /api/auth/providers failed: the chooser says so and offers a retry, never a Lark-only page. */
  const providersFailed = ref(false)
  /** The outcome of a link/connect round trip, consumed by the Connections page. */
  const connectionsNotice = ref<ConnectionsNotice | null>(null)
  /** An invitation into an organisation account, waiting for the sign-in that accepts it. */
  const invitation = ref<InvitationProof | null>(readStoredInvitation())
  /** What that invitation is for (POST /api/auth/invitations/preview), for the chooser to say. */
  const invitationPreview = ref<InvitationPreviewResponse | null>(null)
  /**
   * The last sign-in was refused with JOIN_REQUESTED: the person is waiting for an
   * admin. The provider says whether the Google super-admin proof is worth offering.
   */
  const joinRequested = ref<{ provider: RedirectProvider } | null>(null)
  /** Hints from a client app's link-out, for the chooser. */
  const hints = ref<SignInHints>(entryHints)

  // Computed
  const isAuthenticated = computed(() => state.value === 'authenticated')
  const isLoading = computed(() => state.value === 'initializing')
  const needsRefresh = computed(() => {
    if (!expiresAt.value) return false
    // Half the token's lifetime: 15 days of Lark's 30, 12 hours of the others' 24.
    return Date.now() > expiresAt.value - refreshBufferFor(JWT_EXPIRATION_MS_BY_PLATFORM[platform.value])
  })

  // API base URL
  const API_BASE = import.meta.env.VITE_API_URL || ''
  const APP_ID = import.meta.env.VITE_LARK_APP_ID

  /** Lark browser login is only offerable if we know which Lark app to ask for. */
  const canUseOAuth = computed(() => !!APP_ID)

  /** Platforms the chooser may offer: enabled here, and with what they need configured. */
  const signInOptions = computed(() => {
    const enabled = providers.value?.platforms ?? ['lark']
    const clients = providers.value?.clients ?? {}
    return {
      lark: canUseOAuth.value,
      microsoft: enabled.includes('microsoft') && !!clients.microsoft?.clientId,
      google: enabled.includes('google') && !!clients.google?.clientId,
      email: enabled.includes('email'),
      turnstileSiteKey: clients.email?.turnstileSiteKey ?? null,
    }
  })

  // ---------------------------------------------------------------------------
  // Providers
  // ---------------------------------------------------------------------------

  let providersRequest: Promise<AuthProvidersResponse> | null = null

  /**
   * GET /api/auth/providers, once it succeeds. A failed request does not read as
   * "Lark only" any more: that hid Microsoft, Google and email from everyone a
   * network blip caught. `providers` stays unknown, `providersFailed` is set (the
   * sign-in chooser offers a retry), and callers that need an answer now get the
   * Lark-only one without it being stored.
   *
   * The chooser cannot fall back to every button instead: Microsoft and Google
   * need the client ids only this answer carries, so a retry is the honest offer.
   */
  function loadProviders(): Promise<AuthProvidersResponse> {
    providersRequest ??= (async () => {
      try {
        const response = await fetch(`${API_BASE}/api/auth/providers`)
        if (!response.ok) throw new Error(String(response.status))
        const data = (await response.json()) as AuthProvidersResponse
        providers.value = {
          platforms: Array.isArray(data.platforms) ? data.platforms : ['lark'],
          clients: data.clients ?? {},
          ...(typeof data.emailSending === 'boolean' ? { emailSending: data.emailSending } : {}),
          ...(typeof data.sign === 'boolean' ? { sign: data.sign } : {}),
        }
        providersFailed.value = false
        return providers.value
      } catch {
        providersFailed.value = true
        // Ask again next time: a blip should not hide Microsoft for the whole visit.
        providersRequest = null
        return providers.value ?? { platforms: ['lark'], clients: {} }
      }
    })()
    return providersRequest
  }

  // ---------------------------------------------------------------------------
  // Session
  // ---------------------------------------------------------------------------

  /** Adopt a successful sign-in, whichever way it came (either answer shape, SignInAnswer). */
  function applySession(loginData: SignInAnswer, persist: boolean): void {
    const jwt = 'jwt' in loginData ? loginData.jwt : loginData.token
    const claims = readJwtClaims(jwt)
    token.value = jwt
    expiresAt.value = loginData.expiresAt ?? claims?.exp ?? null
    platform.value = claims?.platform ?? 'lark'
    user.value = {
      userId: loginData.user?.id ?? claims?.userId ?? '',
      name: loginData.user?.name || 'Unknown',
      avatarUrl: loginData.user && 'avatar_url' in loginData.user ? loginData.user.avatar_url : undefined,
      tenantKey: loginData.tenant?.key ?? claims?.tenantKey ?? '',
      tenantName: loginData.tenant?.name ?? undefined,
    }

    state.value = 'authenticated'
    error.value = null
    joinRequested.value = null
    // Accepted (or the person was already in): the invitation has done its work.
    dropInvitation()

    isBrowserSession.value = persist
    if (persist) saveSession()
    setupVisibilityListener()
  }

  function saveSession(): void {
    if (!token.value || !expiresAt.value || !user.value) return
    const session: StoredSession = { jwt: token.value, expiresAt: expiresAt.value, user: user.value, platform: platform.value }
    try {
      localStorage.setItem(SESSION_KEY, JSON.stringify(session))
    } catch {
      // Private-browsing quota failures are not worth failing a login over —
      // the session simply lasts until the tab is reloaded.
    }
  }

  function clearStoredSession(): void {
    try {
      localStorage.removeItem(SESSION_KEY)
    } catch {
      // See saveSession.
    }
    // Per-session data other pages left in this tab goes with the session.
    clearApprovalRequestsCache()
  }

  /**
   * Restore a browser session from a previous visit.
   *
   * Only used outside Lark. Inside it, `tt.requestAccess()` is silent and also
   * refreshes the Lark OAuth tokens the server holds for Base API calls, so
   * short-circuiting it to save a round trip would be a false economy.
   */
  async function restoreSession(): Promise<boolean> {
    let stored: StoredSession | null = null
    try {
      const raw = localStorage.getItem(SESSION_KEY)
      stored = raw ? (JSON.parse(raw) as StoredSession) : null
    } catch {
      stored = null
    }

    if (!stored?.jwt || !stored.expiresAt || !stored.user) {
      clearStoredSession()
      return false
    }

    token.value = stored.jwt
    expiresAt.value = stored.expiresAt
    user.value = stored.user
    // The token's own claim wins over what was stored beside it.
    platform.value = readJwtClaims(stored.jwt)?.platform ?? stored.platform ?? 'lark'
    isBrowserSession.value = true

    // An expired token can still be refreshed (the API accepts it for that):
    // a Microsoft or Google JWT lives a day, and someone back after a weekend
    // should pass straight through while their provider still vouches for
    // them. Only when that fails is it the chooser.
    if (stored.expiresAt <= Date.now()) {
      if (!(await refresh())) {
        forgetSession()
        // What refresh() reported is no news here: a stored session that has run out
        // (a Lark browser session after its 30 days, a provider that no longer vouches)
        // leads to the plain sign-in page, as it always did, not to an error banner.
        error.value = null
        return false
      }
    }

    state.value = 'authenticated'
    setupVisibilityListener()
    // Renew in the background rather than making the customer wait; a failure
    // here is handled by refresh() and leaves the current token in place.
    if (needsRefresh.value) void refresh()
    return true
  }

  // ---------------------------------------------------------------------------
  // Entry points
  // ---------------------------------------------------------------------------

  /**
   * Initialize authentication
   * This should be called once on app startup
   */
  async function initialize(): Promise<void> {
    state.value = 'initializing'
    error.value = null

    // An email link is a sign-in by itself; it outranks a stored session (the
    // person may be switching accounts) and never needs Lark.
    if (emailLink) {
      const link = emailLink
      emailLink = null
      await completeEmailLink(link)
      return
    }

    // Returning from a provider's screen takes priority: the code is
    // single-use and waiting in the URL right now.
    if (callback) {
      const returned = callback
      // Consumed: the code is single-use and the nonce is about to be, so a
      // retry() must start a fresh round trip rather than replay a spent pair.
      callback = null
      await completeOAuth(returned)
      return
    }

    await establishSession()
  }

  /**
   * The session this page would have without any callback: Lark's silent
   * sign-in inside the client, else a stored browser session, else the
   * chooser. True when signed in.
   */
  async function establishSession(): Promise<boolean> {
    // index.html inserts the JSSDK without blocking the first paint, so it may
    // still be on its way; whether this is Lark is only known once it has run.
    await window.larkSdkReady
    if (typeof window.h5sdk?.ready === 'function') {
      await initializeInLark()
      return state.value === 'authenticated'
    }

    // An invitation outranks a stored session, as an email link does: the person
    // who opened it signs in with the inviting account's platform.
    if (!invitation.value && (await restoreSession())) return true

    // No error: this is the ordinary first visit in a browser, and the gate it
    // renders is an invitation to sign in, not a failure to explain.
    state.value = 'not_in_lark'
    void loadProviders().then(() => autoStartFromHints())
    return false
  }

  // ---------------------------------------------------------------------------
  // Invitations (organisation accounts)
  // ---------------------------------------------------------------------------

  function dropInvitation(): void {
    invitation.value = null
    invitationPreview.value = null
    try {
      sessionStorage.removeItem(INVITATION_KEY)
    } catch {
      // Nothing was stored.
    }
  }

  /**
   * Who invited the person, to which account, and which platform signs them in. The
   * chooser asks it each time it shows (a failed round trip comes back to it too).
   */
  async function loadInvitationPreview(): Promise<void> {
    const proof = invitation.value
    if (!proof || invitationPreview.value) return
    try {
      invitationPreview.value = await postJson<InvitationPreviewResponse>('/api/auth/invitations/preview', proof)
    } catch (err) {
      // Expired, withdrawn or used: say so, and offer the ordinary chooser. A network
      // failure keeps the invitation: the next visit to the chooser asks again.
      if (err instanceof AuthApiError && err.code === 'INVITATION_INVALID') dropInvitation()
      // A fetch that never reached the API says so in English ("Failed to fetch").
      error.value = err instanceof AuthApiError ? errorKey(err) : 'authFailed'
    }
  }

  /**
   * "Sign in another way": the invitation is set aside, and a session this browser
   * already had (which the invitation outranked) is taken up again.
   */
  async function dismissInvitation(): Promise<void> {
    dropInvitation()
    error.value = null
    if (await restoreSession()) return
    state.value = 'not_in_lark'
  }

  /** The invitation for a sign-in body, while one is waiting. */
  function invitationBody(): { invitation?: InvitationProof } {
    return invitation.value ? { invitation: invitation.value } : {}
  }

  /**
   * What a refused sign-in leaves behind: JOIN_REQUESTED marks the person as waiting
   * (the chooser explains it), and an invitation the API refused is not sent again.
   */
  function noteRefusal(err: unknown, provider: RedirectProvider): void {
    if (!(err instanceof AuthApiError)) return
    if (err.code === 'JOIN_REQUESTED') joinRequested.value = { provider }
    if (err.code === 'INVITATION_INVALID' || err.code === 'INVITATION_WRONG_ORGANISATION') dropInvitation()
  }

  /**
   * A client app sent the person here naming their provider (40 §1.7): start
   * that provider straight away with `login_hint`, so a browser already signed
   * in to Microsoft or Google passes through without a click. Once per page.
   */
  let hintsUsed = false
  function autoStartFromHints(): void {
    // Not spent on a failed providers request: the retry that succeeds uses them.
    if (hintsUsed || state.value !== 'not_in_lark' || !providers.value) return
    // The public IT admin page is read by someone who is not signing in at all.
    if (window.location.hash.startsWith(`#${MICROSOFT_ADMIN_PAGE_PATH}`)) return
    hintsUsed = true
    const { provider, hint } = hints.value
    if (provider === 'microsoft' && signInOptions.value.microsoft) void startSignIn('microsoft', { loginHint: hint })
    else if (provider === 'google' && signInOptions.value.google) void startSignIn('google', { loginHint: hint })
  }

  /** The chooser's Retry: ask for the providers again, then honour a client app's hint. */
  async function retryProviders(): Promise<void> {
    await loadProviders()
    autoStartFromHints()
  }

  /** Login-free flow, available only inside the Lark client. */
  async function initializeInLark(): Promise<void> {
    if (!APP_ID) {
      console.error('[Auth] No app ID configured')
      state.value = 'error'
      error.value = 'authErrorAppNotConfigured'
      return
    }

    try {
      // Step 1: Wait for h5sdk.ready (NO config needed for requestAccess!)
      await new Promise<void>((resolve) => {
        window.h5sdk.ready(resolve)
      })

      // Step 2: Get auth code via tt.requestAccess
      const code = await new Promise<string>((resolve, reject) => {
        window.tt.requestAccess({
          appID: APP_ID,
          scopeList: [...LARK_OAUTH_SCOPES_BASE],
          success: (result) => resolve(result.code),
          fail: (err) => {
            console.error('[Auth] requestAccess failed:', err)
            // Error 999 with errno 2700002 means app not installed/authorized
            if (err.errCode === 999) {
              reject({ type: 'app_not_installed', message: err.errString || err.errMsg })
            } else {
              reject(new Error(`requestAccess failed: ${err.errCode} - ${err.errMsg}`))
            }
          }
        })
      })

      // Step 3: Exchange code for token via backend
      // For tt.requestAccess() flow, redirect_uri should be current page URL without ? and #
      // See: https://open.larksuite.com/document/home/develop-a-gadget/login-free-process
      const redirectUri = window.location.href.split('?')[0].split('#')[0]
      const loginData = await exchangeLarkCode(code, redirectUri)
      console.log('[Auth] Authenticated:', loginData.user?.name, '@', loginData.tenant?.name)

      // Not persisted: this path is silent and re-runs on every load anyway.
      applySession(loginData, false)
    } catch (err: any) {
      console.error('[Auth] Failed:', err)
      if (err?.type === 'app_not_installed') {
        state.value = 'app_not_installed'
        // The dedicated app_not_installed screen carries its own copy, so
        // there is no message to render — just do not leave a stale one.
        error.value = null
      } else {
        noteRefusal(err, 'lark')
        state.value = 'error'
        error.value = errorKey(err)
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Redirect round trips
  // ---------------------------------------------------------------------------

  /** Keep the round trip, or say why we cannot (nowhere to keep the verifier). */
  function rememberRoundTrip(trip: PendingRoundTrip): boolean {
    try {
      sessionStorage.setItem(ROUND_TRIP_KEY, JSON.stringify(trip))
      return true
    } catch {
      // Without somewhere to keep the state the callback cannot be verified,
      // and an unverifiable callback is one we would have to reject anyway.
      return false
    }
  }

  /** The round trip this callback belongs to, consumed whatever happens next. */
  function takeRoundTrip(returnedState: string | null): PendingRoundTrip | null {
    let trip: PendingRoundTrip | null = null
    let legacyState: string | null = null
    let legacyReturn: string | null = null
    try {
      const raw = sessionStorage.getItem(ROUND_TRIP_KEY)
      trip = raw ? (JSON.parse(raw) as PendingRoundTrip) : null
      legacyState = sessionStorage.getItem(LEGACY_STATE_KEY)
      legacyReturn = sessionStorage.getItem(LEGACY_RETURN_KEY)
      sessionStorage.removeItem(ROUND_TRIP_KEY)
      sessionStorage.removeItem(LEGACY_STATE_KEY)
      sessionStorage.removeItem(LEGACY_RETURN_KEY)
    } catch {
      return null
    }
    if (!returnedState) return null
    if (trip && trip.state === returnedState && Date.now() - trip.createdAt < ROUND_TRIP_MAX_AGE_MS) return trip
    if (legacyState && legacyState === returnedState) {
      return {
        state: legacyState,
        provider: 'lark',
        intent: 'signin',
        codeVerifier: '',
        nonce: '',
        redirectUri: oauthRedirectUri(),
        returnTo: legacyReturn ?? undefined,
        createdAt: Date.now(),
      }
    }
    return null
  }

  /**
   * Send the browser to a provider's screen. Does not return on success.
   *
   * What each provider is asked for is the API's word wherever the API decides
   * it: a data connection asks POST /api/tenant/connections/{provider} first and
   * builds its URL from that plan (Microsoft: the person's own tenant and the
   * purpose's scopes, which /complete redeems with; Google: drive.file, and
   * prompt=consent only when no grant is held), so the two halves of a round
   * trip cannot disagree. Sign-in, linking and the Google admin proof use the
   * shared-types scope lists the API redeems them with.
   */
  async function startRoundTrip(
    provider: RedirectProvider,
    intent: RoundTripIntent,
    options: { purpose?: ConnectionPurpose; loginHint?: string; returnTo?: string; consentRetry?: boolean; demoteFirstUser?: boolean; adminProof?: boolean } = {},
  ): Promise<void> {
    const clients = (providers.value ?? (await loadProviders())).clients
    const redirectUri = oauthRedirectUri()
    const oauthState = makeOAuthState(provider)
    const codeVerifier = provider === 'lark' ? '' : makeCodeVerifier()
    const nonce = provider === 'lark' ? '' : randomUrlSafe(24)
    const trip: PendingRoundTrip = {
      state: oauthState,
      provider,
      intent,
      purpose: options.purpose,
      codeVerifier,
      nonce,
      redirectUri,
      returnTo: options.returnTo ?? currentRoute(),
      consentRetry: options.consentRetry,
      demoteFirstUser: options.demoteFirstUser,
      adminProof: options.adminProof,
      createdAt: Date.now(),
    }

    let url: string
    if (provider === 'lark') {
      if (!APP_ID) return failStart('authErrorAppNotConfigured')
      url = buildLarkAuthorizeUrl({
        appId: APP_ID,
        redirectUri,
        state: oauthState,
        // offline_access included, matching the extension's long-working browser
        // login against these same apps: a browser session is not re-authorised
        // silently the way tt.requestAccess() is, so it wants a refresh token.
        scopes: LARK_OAUTH_SCOPES,
      })
    } else {
      const clientId = clients[provider]?.clientId
      if (!clientId) return failStart('authErrorProviderUnavailable')
      const codeChallenge = await pkceChallenge(codeVerifier)
      const common = { clientId, redirectUri, state: oauthState, nonce, codeChallenge }

      if (intent === 'connect') {
        if (!options.purpose) return failStart('authFailed')
        let plan: MicrosoftConnectResponse | GoogleConnectPlan
        try {
          plan = await postJson<MicrosoftConnectResponse | GoogleConnectPlan>(`/api/tenant/connections/${provider}`, { purpose: options.purpose }, true)
        } catch (err) {
          return failStart(errorKey(err))
        }
        if (provider === 'microsoft') {
          if (!('tenant' in plan)) return failStart('authFailed')
          url = buildMicrosoftAuthorizeUrl({ ...common, scopes: plan.scopes, tenant: plan.tenant })
        } else {
          if (!('prompt' in plan)) return failStart('authFailed')
          url = buildGoogleAuthorizeUrl({ ...common, scopes: plan.scopes, prompt: plan.prompt ?? undefined, loginHint: plan.loginHint })
        }
      } else if (provider === 'microsoft') {
        url = buildMicrosoftAuthorizeUrl({ ...common, scopes: MICROSOFT_SIGNIN_SCOPES, loginHint: options.loginHint })
      } else if (intent === 'takeover' || options.adminProof) {
        // The one-shot Workspace super-admin proof (google.ts GOOGLE_ADMIN_PROOF_SCOPES):
        // online and not incremental, so nothing outlives the API's one Directory call.
        // As a sign-in it admits a super admin whatever the join policy says.
        url = buildGoogleAuthorizeUrl({
          ...common,
          scopes: GOOGLE_ADMIN_PROOF_SCOPES,
          accessType: 'online',
          includeGrantedScopes: false,
          prompt: 'select_account',
          loginHint: options.loginHint,
        })
      } else {
        url = buildGoogleAuthorizeUrl({
          ...common,
          scopes: GOOGLE_SIGNIN_SCOPES,
          loginHint: options.loginHint,
          // Google returns a refresh token only on a consent screen; the API asks
          // for one (GOOGLE_CONSENT_REQUIRED) only when it holds none for the person.
          prompt: options.consentRetry ? 'consent' : undefined,
        })
      }
    }

    if (!rememberRoundTrip(trip)) return failStart('authErrorStorageUnavailable')
    oauthRedirecting.value = true
    window.location.href = url
  }

  /** A round trip that could not start: say so where the person is. */
  function failStart(message: string): void {
    if (state.value === 'authenticated') {
      connectionsNotice.value = { kind: 'error', message }
    } else {
      state.value = 'not_in_lark'
      error.value = message
    }
  }

  /** Sign in with a redirect provider (the chooser's buttons). */
  function startSignIn(provider: RedirectProvider, options: { loginHint?: string; adminProof?: boolean } = {}): Promise<void> {
    error.value = null
    joinRequested.value = null
    signInConsentUrl.value = null
    microsoftDeclined.value = false
    return startRoundTrip(provider, 'signin', options)
  }

  /** The Lark button, kept under its old name for the views that call it. */
  function loginWithOAuth(): void {
    void startSignIn('lark')
  }

  /** Connect a data scope with the person's own Microsoft or Google sign-in. */
  function startConnect(provider: OAuthProvider, purpose: ConnectionPurpose): Promise<void> {
    connectionsNotice.value = null
    return startRoundTrip(provider, 'connect', { purpose, returnTo: '#/integrations' })
  }

  /**
   * "I administer this Google Workspace" (design §3.3, decision §11.2): a super
   * admin of the account's Workspace becomes admin, and may take admin from
   * whoever got it by signing in first.
   */
  function startTakeover(demoteFirstUser: boolean): Promise<void> {
    connectionsNotice.value = null
    return startRoundTrip('google', 'takeover', { returnTo: '#/integrations', demoteFirstUser })
  }

  /** Finish whatever round trip this callback ends. */
  async function completeOAuth(returned: OAuthCallback): Promise<void> {
    // Always strip, including on the failure paths below — the code is spent
    // either way and must not survive into a reload.
    stripCallbackParams()

    // Microsoft's admin consent: the API's link, opened by an admin who is
    // usually someone else in another browser. The API reads its own signed
    // state; nothing is granted by this answer either way.
    if (isMicrosoftAdminConsentState(returned.state)) {
      await completeAdminConsent(returned)
      return
    }

    const trip = takeRoundTrip(returned.state)
    const provider = providerOfOAuthState(returned.state)

    if (!trip || !provider || trip.provider !== provider) {
      console.error('[Auth] OAuth state mismatch — discarding the callback')
      // The session this page had, if any, is still good; only the callback is not.
      if (await establishSession()) {
        connectionsNotice.value = { kind: 'error', message: 'authErrorStateMismatch' }
      } else {
        error.value = 'authErrorStateMismatch'
      }
      return
    }

    if (trip.intent === 'signin') {
      await completeSignIn(trip, returned)
      return
    }

    // Linking, connecting and the takeover act on the session the person
    // already had (restored, or Lark's silent one inside the client).
    if (!(await establishSession())) {
      error.value = 'authErrorSignInToFinish'
      return
    }
    connectionsNotice.value = await completeAccountAction(trip, returned)
    await goTo(trip.returnTo ?? '#/integrations')
  }

  /** POST /api/auth/microsoft/admin-consent with what Microsoft put on the redirect. */
  async function completeAdminConsent(returned: OAuthCallback): Promise<void> {
    // The IT admin page's own links (MICROSOFT_ORGANISATION_APPROVAL_STATE): nobody's
    // request, no signed state to read back, and nothing on our side depends on the
    // answer, so it is read here only to thank the admin, back on the page they came from.
    if (returned.state === MICROSOFT_ORGANISATION_APPROVAL_STATE) {
      const approved = returned.kind === 'adminConsent' && returned.granted
      // On the page first, so a signed-out admin never sees the chooser flash in between.
      await goTo(`#${MICROSOFT_ADMIN_PAGE_PATH}`)
      const signedIn = await establishSession()
      if (signedIn) {
        connectionsNotice.value = approved ? { kind: 'adminConsentGranted' } : { kind: 'error', message: 'authErrorConsentRefused' }
      } else {
        notice.value = approved ? 'adminConsentDone' : null
        error.value = approved ? null : 'authErrorConsentRefused'
      }
      if (signedIn) await goTo('#/integrations')
      return
    }
    let granted = false
    try {
      const answer = await postJson<MicrosoftAdminConsentResultResponse>('/api/auth/microsoft/admin-consent', {
        state: returned.state,
        adminConsent: returned.kind === 'adminConsent' ? (returned.granted ? 'True' : 'False') : undefined,
        tenant: returned.kind === 'adminConsent' ? (returned.tenant ?? undefined) : undefined,
        error: returned.kind === 'error' ? returned.error : undefined,
        errorDescription: returned.kind === 'error' ? (returned.description ?? undefined) : undefined,
      })
      granted = answer.granted
    } catch (err) {
      // An expired or foreign state (ATTEMPT_INVALID): Microsoft has recorded the
      // admin's answer anyway; only our thank-you cannot be sure which it was.
      console.warn('[Auth] Admin consent answer not read:', err)
    }
    if (await establishSession()) {
      connectionsNotice.value = granted ? { kind: 'adminConsentGranted' } : { kind: 'error', message: 'authErrorConsentRefused' }
    } else {
      notice.value = granted ? 'adminConsentDone' : null
      error.value = granted ? null : 'authErrorConsentRefused'
    }
  }

  async function completeSignIn(trip: PendingRoundTrip, returned: OAuthCallback): Promise<void> {
    if (returned.kind !== 'code') {
      const declined = personDeclined(trip, returned)
      // Microsoft's refusal goes to the API, which turns "an admin must consent"
      // into MS_CONSENT_REQUIRED with the admin-consent link for the chooser.
      if (trip.provider === 'microsoft' && returned.kind === 'error' && !declined) {
        try {
          await postJson('/api/auth/microsoft/login', { error: returned.error, errorDescription: returned.description ?? undefined })
        } catch (err) {
          state.value = 'not_in_lark'
          error.value = errorKey(err)
          signInConsentUrl.value = consentUrlOf(err)
          return
        }
      }
      state.value = 'not_in_lark'
      error.value = declined ? 'authErrorCancelled' : 'authFailed'
      // Microsoft's "needs admin approval" screen can also end in a plain access_denied
      // (the person backs out of it): the chooser then offers the IT admin page too.
      microsoftDeclined.value = declined && trip.provider === 'microsoft'
      return
    }
    try {
      const loginData =
        trip.provider === 'lark'
          ? await exchangeLarkCode(returned.code, trip.redirectUri)
          : await postJson<SignInAnswer>(`/api/auth/${trip.provider}/login`, {
              code: returned.code,
              redirectUri: trip.redirectUri,
              codeVerifier: trip.codeVerifier,
              nonce: trip.nonce,
              locale: getLocale(),
              ...invitationBody(),
            })
      console.log('[Auth] Authenticated via', trip.provider)
      applySession(loginData, true)
      if (trip.returnTo) await goTo(trip.returnTo)
    } catch (err) {
      // Google sends a refresh token only from a consent screen; a returning
      // person whose grant the API no longer holds is asked once more, with it.
      if (trip.provider === 'google' && err instanceof AuthApiError && err.code === 'GOOGLE_CONSENT_REQUIRED' && !trip.consentRetry && !trip.adminProof) {
        await startRoundTrip('google', 'signin', { returnTo: trip.returnTo, consentRetry: true })
        return
      }
      // The super-admin proof is asked online (no refresh token): GOOGLE_CONSENT_REQUIRED
      // there means Google did not vouch for a super admin, so the person is still waiting.
      // It also covers someone who picked another Google account on the proof screen (it
      // offers select_account): that account is not a super admin of this domain either,
      // and the waiting box lets them try the right one.
      if (trip.adminProof && err instanceof AuthApiError && err.code === 'GOOGLE_CONSENT_REQUIRED') {
        joinRequested.value = { provider: 'google' }
        state.value = 'not_in_lark'
        error.value = 'authErrorJoinRequested'
        return
      }
      console.error('[Auth] Sign-in failed:', err)
      noteRefusal(err, trip.provider)
      // Back to the chooser rather than the generic error screen: the useful
      // next action is "try signing in again", which only the chooser offers.
      state.value = 'not_in_lark'
      error.value = errorKey(err)
      signInConsentUrl.value = consentUrlOf(err)
    }
  }

  /**
   * The person said no on the provider's screen (access_denied). Not when Microsoft's
   * access_denied carries an admin-consent code (AADSTS65001 / 90094 / 90095): that is
   * the organisation's policy, which the API answers with MS_CONSENT_REQUIRED.
   */
  function personDeclined(trip: PendingRoundTrip, returned: OAuthCallback): boolean {
    if (returned.kind !== 'error' || returned.error !== 'access_denied') return false
    return !(trip.provider === 'microsoft' && isMicrosoftConsentRequired({ error: returned.error, errorDescription: returned.description }))
  }

  /** The admin-consent link an MS_CONSENT_REQUIRED refusal carries, if any. */
  function consentUrlOf(err: unknown): string | null {
    if (!(err instanceof AuthApiError) || err.code !== 'MS_CONSENT_REQUIRED') return null
    const url = err.body?.adminConsentUrl
    return typeof url === 'string' && url.startsWith('https://login.microsoftonline.com/') ? url : null
  }

  /** Connect a data scope, or take over a Workspace account. */
  async function completeAccountAction(trip: PendingRoundTrip, returned: OAuthCallback): Promise<ConnectionsNotice> {
    if (trip.provider === 'lark') return { kind: 'error', message: 'authFailed' }
    const cancelled = personDeclined(trip, returned)

    try {
      if (trip.intent === 'connect') {
        const purpose = trip.purpose ?? (trip.provider === 'google' ? 'sheets' : 'files')
        if (trip.provider === 'microsoft' && returned.kind === 'error' && !cancelled) {
          // Microsoft's refusal on the redirect: the API reads it, and answers
          // MS_CONSENT_REQUIRED with the admin-consent link when that is the cause.
          await postJson('/api/tenant/connections/microsoft/complete', { purpose, error: returned.error, errorDescription: returned.description ?? undefined }, true)
          return { kind: 'error', message: 'authFailed' }
        }
        if (returned.kind !== 'code') return { kind: 'error', message: cancelled ? 'authErrorConnectCancelled' : 'authFailed' }
        await postJson(
          `/api/tenant/connections/${trip.provider}/complete`,
          { purpose, code: returned.code, redirectUri: trip.redirectUri, codeVerifier: trip.codeVerifier, nonce: trip.nonce },
          true,
        )
        return { kind: 'connected', provider: trip.provider, purpose }
      }

      if (returned.kind !== 'code') return { kind: 'error', message: cancelled ? 'authErrorConnectCancelled' : 'authFailed' }
      const proof = { code: returned.code, redirectUri: trip.redirectUri, codeVerifier: trip.codeVerifier, nonce: trip.nonce }

      await postJson<GoogleTakeoverResponse>('/api/tenant/connections/google/takeover', { ...proof, demoteFirstUser: trip.demoteFirstUser === true }, true)
      return { kind: 'tookOver' }
    } catch (err) {
      console.error('[Auth] Account action failed:', err)
      if (err instanceof AuthApiError && err.code === 'MS_CONSENT_REQUIRED') {
        return { kind: 'consentRequired', purpose: trip.purpose ?? 'files', adminConsentUrl: consentUrlOf(err) }
      }
      return { kind: 'error', message: errorKey(err) }
    }
  }

  /** A failed call's message: our own key for codes the chooser explains, else the server's text. */
  function errorKey(err: unknown): string {
    if (err instanceof AuthApiError) {
      switch (err.code) {
        case 'PERSONAL_ACCOUNT': return 'authErrorPersonalAccount'
        case 'EMAIL_CODE_REQUIRED': return 'authErrorEmailCodeRequired'
        case 'PLATFORM_DISABLED': return 'authErrorProviderUnavailable'
        case 'MEMBER_REMOVED': return 'authErrorMemberRemoved'
        // Membership (handlers/members.ts, utils/membership.ts)
        case 'JOIN_REQUESTED': return 'authErrorJoinRequested'
        case 'INVITATION_INVALID': return 'authErrorInvitationInvalid'
        case 'INVITATION_WRONG_ORGANISATION': return 'authErrorInvitationWrongOrganisation'
        case 'USER_IN_OTHER_ACCOUNT': return 'authErrorOtherAccount'
        case 'EMAIL_NOT_VERIFIED': return 'authErrorEmailNotVerified'
        case 'RATE_LIMITED': return 'authErrorRateLimited'
        // Microsoft (handlers/microsoftAuth.ts)
        case 'MS_CONSENT_REQUIRED': return 'authErrorMicrosoftConsent'
        case 'MS_CONSENT_DECLINED': return 'authErrorCancelled'
        case 'MS_SIGNIN_FAILED': return 'authFailed'
        case 'MS_NOT_CONFIGURED': return 'authErrorProviderUnavailable'
        // Google (handlers/googleAuth.ts)
        case 'GOOGLE_NOT_CONFIGURED': return 'authErrorProviderUnavailable'
        case 'GOOGLE_CONSENT_REQUIRED': return 'authFailed'
        case 'CODE_INVALID': return 'authErrorStateMismatch'
        case 'SCOPE_NOT_GRANTED': return 'authErrorScopeNotGranted'
        case 'NOT_WORKSPACE_ADMIN': return 'authErrorNotWorkspaceAdmin'
        case 'NOT_A_WORKSPACE_ACCOUNT': return 'authErrorNotWorkspace'
        case 'WORKSPACE_MISMATCH': return 'authErrorWorkspaceMismatch'
        // Both providers' connect routes
        case 'CONNECTION_USER_MISMATCH': return 'authErrorConnectionUserMismatch'
        case 'PLATFORM_MISMATCH': return 'authErrorProviderUnavailable'
        case 'FRESH_TOKEN_REQUIRED': return 'authErrorSessionExpired'
      }
    }
    return err instanceof Error && err.message ? err.message : 'authFailed'
  }

  /**
   * Move to a hash route after a round trip (the redirect lands on the bare
   * origin). Through the router, once its first navigation has settled: set on
   * location.hash, a deep link was overwritten when that navigation finished
   * after us (a lazily loaded page) and replaced the URL with "/". Imported
   * here, not at the top: the router's guard reads the stores.
   */
  async function goTo(route: string): Promise<void> {
    if (!route.startsWith('#/')) return
    try {
      const { default: router } = await import('@/router')
      await router.isReady()
      const target = route.slice(1)
      if (router.currentRoute.value.fullPath !== target) await router.replace(target)
    } catch (err) {
      // Staying where we are is the worst outcome; the sign-in itself stands.
      console.warn('[Auth] Could not return to', route, err)
    }
  }

  // ---------------------------------------------------------------------------
  // Email
  // ---------------------------------------------------------------------------

  /** Send the link and code. Answers the challenge the code is entered against. Throws with a message key. */
  async function emailStart(email: string, turnstileToken: string): Promise<EmailStartResponse> {
    const body: EmailStartRequest = { email, turnstileToken, locale: getLocale() as EmailStartRequest['locale'] }
    try {
      return await postJson<EmailStartResponse>('/api/auth/email/start', body)
    } catch (err) {
      throw new Error(emailErrorKey(err))
    }
  }

  /** The 6-digit code typed in on the chooser. Throws with a message key on refusal. */
  async function emailVerifyCode(challengeId: string, code: string): Promise<void> {
    try {
      const body: EmailVerifyRequest = { challengeId, code }
      applySession(await postJson<ClientSessionResponse>('/api/auth/email/verify', body), true)
    } catch (err) {
      throw new Error(emailErrorKey(err))
    }
  }

  /** A sign-in or invitation link from the mail: its proof is the verify body as it stands. */
  async function completeEmailLink(link: EmailLinkProof): Promise<void> {
    try {
      const body: EmailVerifyRequest = link
      applySession(await postJson<ClientSessionResponse>('/api/auth/email/verify', body), true)
    } catch (err) {
      console.error('[Auth] Email link refused:', err)
      state.value = 'not_in_lark'
      error.value = emailErrorKey(err)
      void loadProviders()
    }
  }

  /** handlers/emailAuth.ts's codes (shared-types EMAIL_AUTH_ERROR_CODES), then the common ones. */
  function emailErrorKey(err: unknown): string {
    if (err instanceof AuthApiError) {
      switch (err.code) {
        case 'EMAIL_CHALLENGE_INVALID': return 'authErrorEmailLinkExpired'
        case 'EMAIL_CODE_INVALID':
          // The guesses are counted before they are checked: the last wrong one spends the challenge.
          return err.body?.attemptsLeft === 0 ? 'authErrorEmailTooManyAttempts' : 'authErrorEmailCodeWrong'
        case 'INVITATION_INVALID': return 'authErrorInvitationInvalid'
        case 'INVALID_EMAIL': return 'authErrorInvalidEmail'
        case 'TURNSTILE_FAILED': return 'authErrorTurnstileFailed'
        case 'TURNSTILE_UNAVAILABLE':
        case 'EMAIL_UNAVAILABLE': return 'authErrorEmailUnavailable'
        case 'EMAIL_DAILY_LIMIT': return 'authErrorEmailDailyLimit'
      }
    }
    return errorKey(err)
  }

  // ---------------------------------------------------------------------------
  // HTTP
  // ---------------------------------------------------------------------------

  /** POST JSON to the API; `authed` sends the session's JWT. Throws AuthApiError. */
  async function postJson<T>(path: string, body: unknown, authed = false): Promise<T> {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (authed && token.value) headers.Authorization = `Bearer ${token.value}`
    const response = await fetch(`${API_BASE}${path}`, { method: 'POST', headers, body: JSON.stringify(body) })
    if (!response.ok) {
      const errorData = (await response.json().catch(() => ({}))) as ApiErrorResponse & Record<string, unknown>
      console.error('[Auth]', path, response.status, errorData.code)
      // Falls back to the i18n key, never to an English sentence: the views
      // render whatever lands in `error`, and `Login failed: 502` would have
      // shown in English in all seven locales. A message the server *did* send
      // is still preferred — it says something specific, and te() lets it through.
      throw new AuthApiError(errorData.error || errorData.message || 'authFailed', errorData.code, response.status, errorData)
    }
    return (await response.json()) as T
  }

  /** Lark's code exchange: the existing POST /api/auth/login, unchanged. */
  function exchangeLarkCode(code: string, redirectUri: string): Promise<AuthLoginResponse> {
    return postJson<AuthLoginResponse>('/api/auth/login', { code, redirectUri, locale: getLocale(), source: 'company_portal', ...invitationBody() })
  }

  /**
   * Retry authentication
   */
  async function retry(): Promise<void> {
    await initialize()
  }

  /**
   * Refresh JWT token
   * Called automatically when app becomes visible and token needs refresh
   */
  async function refresh(): Promise<boolean> {
    if (!token.value) return false

    try {
      console.log('[Auth] Refreshing token...')
      const response = await fetch(`${API_BASE}/api/auth/refresh`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token.value}`,
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        const errorData: ApiErrorResponse = await response.json().catch(() => ({ error: 'Refresh failed' }))
        console.error('[Auth] Refresh failed:', errorData)
        // Any rejection of the token itself — the refresh token expired, the
        // server session was signed out or the person removed
        // (SESSION_REVOKED), the platform switched off here
        // (PLATFORM_DISABLED). Restored browser sessions are the reason this
        // matters: leaving the localStorage entry in place would resurrect the
        // same dead token on every reload, with the UI insisting it is signed
        // in. A 503 (the provider could not be asked) keeps the token: it is
        // still good until it expires, and the next visibility change tries again.
        if (response.status === 401 || response.status === 403) clearSession(errorData.code)
        return false
      }

      const data: AuthRefreshResponse = await response.json()

      if (!data.success) {
        return false
      }

      // Update token and expiry
      token.value = data.jwt
      expiresAt.value = data.expiresAt
      // Only rewrite what is already persisted: an in-Lark session is
      // deliberately not stored, and refreshing must not start storing it.
      if (localStorage.getItem(SESSION_KEY)) saveSession()
      console.log('[Auth] Token refreshed successfully')

      return true
    } catch (err) {
      console.error('[Auth] Refresh error:', err)
      return false
    }
  }

  /**
   * Handle visibility change - refresh token when app becomes visible
   */
  function handleVisibilityChange(): void {
    if (document.visibilityState === 'visible' && state.value === 'authenticated' && needsRefresh.value) {
      refresh()
    }
  }

  /**
   * Set up visibility change listener for auto-refresh
   */
  function setupVisibilityListener(): void {
    // addEventListener de-duplicates an identical handler reference, so the
    // repeated calls from the several login paths add only one listener.
    document.addEventListener('visibilitychange', handleVisibilityChange)
  }

  /**
   * Clean up visibility listener
   */
  function cleanupVisibilityListener(): void {
    document.removeEventListener('visibilitychange', handleVisibilityChange)
  }

  function forgetSession(): void {
    cleanupVisibilityListener()
    clearStoredSession()
    token.value = null
    expiresAt.value = null
    user.value = null
    platform.value = 'lark'
    connectionsNotice.value = null
    // Kept in step across every teardown: this flag is the only gate on the
    // sign-out entry, and two teardown paths that disagree about it is how
    // that entry ends up offered against a session that no longer exists.
    isBrowserSession.value = false
  }

  /**
   * Sign out of a browser session.
   *
   * A Lark session is forgotten locally only: it does not revoke the Lark
   * authorization (whose tokens run the person's template automations) or sign
   * the person out of Lark itself, so signing back in is one click. Any other
   * platform's session is a server session, signed out there too
   * (POST /api/auth/logout) — its connection stays, for the same reason.
   *
   * No error is set: this outcome was asked for, and the chooser would
   * otherwise render it as a failure the customer has to read past.
   */
  function logout(): void {
    const jwt = token.value
    if (jwt && platform.value !== 'lark') {
      void fetch(`${API_BASE}/api/auth/logout`, { method: 'POST', headers: { Authorization: `Bearer ${jwt}` } }).catch(() => {})
    }
    forgetSession()
    error.value = null
    state.value = 'not_in_lark'
    void loadProviders()
  }

  /**
   * Clear session due to auth error.
   *
   * A browser session goes back to the chooser with a reason; inside the Lark
   * client there is no chooser, so the error screen offers a retry, which
   * re-runs the silent sign-in.
   */
  function clearSession(code?: string): void {
    const wasBrowser = isBrowserSession.value
    forgetSession()
    error.value =
      code === 'SESSION_REVOKED'
        ? 'authErrorSessionRevoked'
        : code === 'PLATFORM_DISABLED'
          ? 'authErrorProviderUnavailable'
          : code === 'MEMBER_REMOVED'
            ? 'authErrorMemberRemoved'
            : code === 'JOIN_REQUESTED'
              ? 'authErrorJoinRequested'
              : 'authErrorSessionExpired'
    if (wasBrowser) {
      state.value = 'not_in_lark'
      void loadProviders()
    } else {
      state.value = 'error'
    }
  }

  return {
    // State
    state,
    user,
    token,
    expiresAt,
    platform,
    error,
    notice,
    signInConsentUrl,
    microsoftDeclined,
    oauthRedirecting,
    isBrowserSession,
    providers,
    providersFailed,
    connectionsNotice,
    hints,
    invitation,
    invitationPreview,
    joinRequested,

    // Computed
    isAuthenticated,
    isLoading,
    needsRefresh,
    canUseOAuth,
    signInOptions,

    // Actions
    initialize,
    retryProviders,
    retry,
    refresh,
    loadProviders,
    loadInvitationPreview,
    dismissInvitation,
    loginWithOAuth,
    startSignIn,
    startConnect,
    startTakeover,
    emailStart,
    emailVerifyCode,
    logout,
    clearSession
  }
})
